import React, { useState, useEffect } from 'react'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { ShiftMoveModalMetaProps, VolunteerWithAllocation, Event, Shift, Area, AllocationStatusItem } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { LoadingSpinner } from './LoadingSpinner'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'

type Props = {
    meta: ShiftMoveModalMetaProps
    isOpen: boolean
    volunteers?: VolunteerWithAllocation[]
    allocations?: AllocationStatusItem[]
    currentShift?: Shift
    sameAreaOnly?: boolean
    onClose: () => void
    onSubmit: (data: { allocation_ids: number[]; status: Array<boolean | null>; shift_id: number; source_shift_id?: number; start_time: string; end_time: string }, moveWithStatus: boolean) => Promise<void>
}

export const ShiftMoveModal = (props: Props) => {
    const { meta, isOpen, volunteers, allocations, currentShift, sameAreaOnly = false, onClose, onSubmit } = props

    const [allEvents, setAllEvents] = useState<Event[]>([])
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
    const [selectedArea, setSelectedArea] = useState<Area | null>(null)
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null)
    const [moveWithStatus, setMoveWithStatus] = useState(true);


    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const restrictToCurrentArea = sameAreaOnly && !!currentShift

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: "include" })
                if (!response.ok) throw new Error("Failed to load events.")
                const data = await response.json()
                setAllEvents(data)
            } catch { setError("Failed to load events") }
        }


        const loadData = async () => {
            setLoading(true)
            await Promise.all([fetchEvents()])
            setLoading(false)
        }

        loadData()
    }, [])



    useEffect(() => {
        if (!isOpen) {
            setSelectedArea(null);
            setSelectedEvent(null);
            setSelectedShift(null);
            setError(null);
            setMoveWithStatus(true);
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen || !restrictToCurrentArea || allEvents.length === 0) return

        const currentEvent = allEvents.find(event => event.areas?.some(area => area.id === currentShift?.area_id)) || null
        const currentArea = currentEvent?.areas?.find(area => area.id === currentShift?.area_id) || null

        setSelectedEvent(currentEvent)
        setSelectedArea(currentArea)
        setSelectedShift(null)
    }, [allEvents, currentShift?.area_id, isOpen, restrictToCurrentArea])


    const handleEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const eventId = Number(e.target.value)
        setSelectedArea(null);
        setSelectedShift(null);
        setSelectedEvent(allEvents.find(ev => ev.id === eventId) || null)
    }

    const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const areaId = Number(e.target.value)
        setSelectedShift(null);
        setSelectedArea(selectedEvent?.areas?.find(a => a.id === areaId) || null)
    }

    const handleShiftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const shiftId = Number(e.target.value)
        setSelectedShift(selectedArea?.shifts?.find(s => s.id === shiftId) || null)
    }

    const selectableEvents = restrictToCurrentArea
        ? allEvents.filter(event => event.areas?.some(area => area.id === currentShift?.area_id))
        : allEvents
    const selectableAreas = restrictToCurrentArea
        ? (selectedEvent?.areas || []).filter(area => area.id === currentShift?.area_id)
        : (selectedEvent?.areas || [])
    const selectableShifts = (selectedArea?.shifts || []).filter(shift => shift.id !== currentShift?.id)


    const handleMoveSubmit = async () => {
        try {
            setLoading(true)
            if (!selectedShift) {
                throw new Error("No Shift is selected.")
            }

            let allocationData;


            // prepare data
            if (allocations) {
                allocationData = allocations.map(allocation => ({ id: allocation.allocation_id, status: allocation.status }));
            }
            else {
                if (!volunteers || !currentShift) {
                    throw new Error("No volunteers are selected.")
                }

                allocationData = volunteers.map(volunteer => {
                    const matchingAllocation = volunteer.allocations.find(
                        allocation => allocation.shift_id === currentShift.id
                    );

                    if (!matchingAllocation) return null;

                    const status = matchingAllocation.allocation_times?.[0]?.status ?? null;

                    return {
                        id: matchingAllocation.id,
                        status: status
                    };
                }).filter(item => item !== null);

            }

            const moveData = {
                allocation_ids: allocationData.map(a => a.id),
                status: allocationData.map(a => a.status),
                shift_id: selectedShift.id,
                source_shift_id: currentShift?.id,
                start_time: selectedShift.start_time,
                end_time: selectedShift.end_time
            };
            await onSubmit(moveData, moveWithStatus);
            onClose();

        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Error while moving volunteers");
        }
        finally {
            setLoading(false);
        }
    }


    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title + ((((volunteers?.length ?? 0) > 1) || ((allocations?.length ?? 0) > 1)) ? 's' : '')}
                maxWidth="md"
                footer={
                    <div className="flex gap-3 justify-end">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.closeButton}
                        </Button>
                        <Button
                            type="submit"
                            form="shift-form"
                            disabled={loading}
                            onClick={handleMoveSubmit}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    </div>
                }
            >
                <div className="px-6 py-4">

                    {loading && (<LoadingSpinner meta={LoadingSpinnerMeta} />)}

                    {!loading && error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}


                    {!loading &&
                        (<>
                            <p className='text-sm text-gray-600 mb-4'>Selected Volunteers: {volunteers?.length ?? allocations?.length}</p>

                            <div className="mb-4">
                                <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{meta.eventLabel}</label>
                                <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleEventChange} value={selectedEvent?.id ?? ""} disabled={restrictToCurrentArea}>
                                    <option value="">{meta.eventPlaceHolder}</option>
                                    {sortByName(selectableEvents).map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
                                </select>
                            </div>
                            {selectedEvent && (
                                <div className="mb-4">
                                    <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">{meta.areaLabel}</label>
                                    <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleAreaChange} value={selectedArea?.id ?? ""} disabled={restrictToCurrentArea}>
                                        <option value="">{meta.areaPlaceHolder}</option>
                                        {sortByName(selectableAreas).map(a => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                    </select>
                                </div>
                            )}
                            {selectedArea && selectedEvent && (
                                <div className="mb-4">
                                    <label className="block mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">{meta.shiftLabel}</label>
                                    <select className="w-full rounded-lg border p-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600" onChange={handleShiftChange} value={selectedShift?.id ?? ""}>
                                        <option value="">{meta.shiftPlaceHolder}</option>
                                        {sortShifts(selectableShifts).map(s => (<option key={s.id} value={s.id}>{getShiftDisplayName(s)}</option>))}
                                    </select>
                                </div>
                            )}
                            {selectedShift &&
                                <label
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition-colors min-h-9"
                                >
                                    <input
                                        type="checkbox"
                                        checked={moveWithStatus}
                                        onChange={() => setMoveWithStatus(prev => !prev)}
                                        className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500 flex-shrink-0"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
                                        Move with acceptance status.
                                    </span>
                                </label>}
                        </>
                        )
                    }
                </div>

            </Modal>


        </>
    )
}
